import React, { useMemo } from 'react'
import { IconButton, makeStyles, Typography } from '@material-ui/core'
import { tableAlternateDark } from '../../services/theme'
import MaterialTable from '@material-table/core'
import { MuiThemeProvider } from '@material-ui/core/styles'
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons'
import useSelectedFacility from '../../hooks/useSelectedFacility'
import StyledAlert from '../Common/StyledAlert'

const useStyles = makeStyles((theme) => ({
	header: {
		padding: '13px 24px',
		userSelect: 'none',
		cursor: 'pointer',
		'& .MuiCardHeader-action': {
			margin: 0
		}
	},
	grid: {
		display: 'grid',
		gridGap: '23px',
		marginBottom: '23px',
		'&:last-child': {
			marginBottom: 0
		}
	},
	gridDouble: {
		gridTemplateColumns: 'repeat(auto-fill, minmax(424px, 1fr))',
		'& .field-wrapper': {
			'& .MuiTextField-root': {
				minWidth: '424px !important'
			}
		}
	},
	rowSplit: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 23
	},
	gridTriple: {
		gridTemplateColumns: 'repeat(auto-fill, minmax(274px, 1fr))',
		'& .field-wrapper': {
			'& .MuiTextField-root': {
				minWidth: '274px !important'
			},
			'& .MuiFormControl-root': {
				minWidth: '272px !important'
			}
		}
	},
	fullWidth: {
		width: '100%'
	},
	expand: {
		transform: 'rotate(0deg)',
		marginLeft: 'auto',
		transition: theme.transitions.create('transform', {
			duration: theme.transitions.duration.shortest
		}),
		padding: 0
	},
	expandOpen: {
		transform: 'rotate(180deg)'
	},
	chips: {
		display: 'flex',
		flexWrap: 'wrap',
		marginTop: '6px',
		backgroundColor: 'transparent !important'
	},
	footerButton: {
		fontFamily: 'Nunito',
		fontWeight: '800',
		'& .MuiButton-label': {
			lineHeight: '19px',
			fontSize: '14px'
		}
	},
	autoComplete: {
		'& .MuiFilledInput-root': {
			paddingTop: '17px'
		},
		'& .Mui-focused': {
			'& .MuiAutocomplete-endAdornment svg': {
				fill: theme.palette.secondary.main
			}
		},
		'& .MuiAutocomplete-endAdornment svg': {
			fill: '#777679'
		}
	},
	chip: {
		marginRight: 4
	},
	sectionHeader: {
		fontWeight: '800',
		fontSize: 16,
		color: '#1A1A35',
		marginBottom: 14,
		marginTop: 24
	},
	stuff: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		flexWrap: 'wrap',
		marginBottom: 16
	},
	theBod: {
		color: '#1A1A35',
		fontWeight: '600',
		fontSize: 14
	},
	expand: {
		fill: '#1A1A35',
		'& .MuiSvgIcon-root': {
			fill: '#1A1A35'
		}
	}
}))

const EXTRA_GROOM_TIME_FEE_CENTS_PER_MIN = 75

const getPetSize = (pet) => {
	if (pet.height) return pet.height.toLowerCase()
	if (pet.weight <= 12) {
		return 'xs'
	} else if (pet.weight >= 13 && pet.weight < 31) {
		return 's'
	} else if (pet.weight >= 31 && pet.weight < 48) {
		return 'm'
	} else if (pet.weight >= 48 && pet.weight < 71) {
		return 'l'
	} else if (pet.weight >= 71) {
		return 'xl'
	} else {
		return null
	}
}

const GroomingDurationTable = ({ pet, prices, addons, toggle, open, facilityOverride, household }) => {
	let { table } = pet
	const size = getPetSize(pet)

	let { facility } = useSelectedFacility()

	if (facilityOverride) {
		facility = facilityOverride
	}

	const extraGroomTime = useMemo(() => {
		return pet?.extra_groom_time ? parseInt(pet.extra_groom_time, 10) : 0
	}, [pet?.extra_groom_time])
	const price_multiplier = facility?.services?.grooming?.price_multiplier || 1

	const extraGroomCostCents = useMemo(() => {
		return extraGroomTime > 0 ? extraGroomTime * EXTRA_GROOM_TIME_FEE_CENTS_PER_MIN : 0
	}, [extraGroomTime])

	const classes = useStyles()

	const petGroomingRates = household?.groomingRates?.[pet?._id]
	if (!petGroomingRates) {
		return <StyledAlert type="info" text="Grooming pricing isn’t available for this pet." style={{ margin: 12 }} />
	}

	const tableData = [
		{

			service: 'bath',
			name: 'Bath & Towel Dry'
		},
		{
			service: 'cleanup',
			name: 'Bath & Blow Out'
		},
		{
			service: 'trim',
			name: 'Touch Up'
		},
		{
			service: 'haircut',
			name: 'Cut & Style',
			trim_amount: 'clip'
		},
	]



	const addToRows = (rows, obj) => {
		const newRows = [
			...rows,
			{
				category: obj.category,
				duration: obj.duration
			}
		]

		return newRows
	}

	const renderServiceTypes = ({ service, trim_amount, name }) => {
		if (name) return <Typography className={classes.theBod}>{name}</Typography>
		return (
			<Typography className={classes.theBod}>
				{trim_amount ? `${trim_amount} ` : ''}
				{service}
			</Typography>
		)
	}

	const getDuration = (service, trim_amount) => {
		// set up the basics...
		let duration = 0
		let rows = []


		if (service === 'bath') {
			const bathPrice = household?.groomingRates[pet._id].bath

			const bathRow = { duration: bathPrice / 100, duration_type: 'pre-work', category: 'extra-time', price: bathPrice }

			return { duration: 30, rows: [bathRow] }
		}

		const isNewPricing = facility?.services?.grooming?.pricing_method === 'new'

		if (isNewPricing && !table?.length) {
			const prices = household?.groomingRates[pet._id]
			const duration = prices[service]
			const rows = [
				{ duration: duration, duration_type: service, category: service, price: prices[service] }
			]
			return { duration, rows }
		}


		let preWork = table.find(
			(t) => t.duration_type === 'pre-work' && t.size.toLowerCase() === size.toLowerCase()
		)
		duration += preWork.duration
		rows = addToRows(rows, preWork)

		let bathBrush = table.find(
			(t) => t.duration_type === 'bath-brush' && t.size.toLowerCase() === size.toLowerCase()
		)
		duration += bathBrush.duration
		rows = addToRows(rows, bathBrush)


		if (service === 'trim') {
			let trim = table.find(
				(t) => t.duration_type === 'trim-finish' && t.size.toLowerCase() === size.toLowerCase()
			)
			duration += trim.duration
			rows = addToRows(rows, trim)

			if (isNewPricing) {

				const trimPrice = household?.groomingRates[pet._id][`base_price_trim`] + extraGroomCostCents
				const trimRow = { duration: 0, duration_type: 'trim-finish', category: 'trim', price: trimPrice }
				rows.push(trimRow)
			}
		}

		if (service === 'cleanup') {
			let cleanup = table.find(
				(t) => t.duration_type === 'cleanup-finish' && t.size.toLowerCase() === size.toLowerCase()
			)
			duration += cleanup.duration
			rows = addToRows(rows, cleanup)

			if (isNewPricing) {
				const cleanupPrice = household?.groomingRates[pet._id][`base_price_cleanup`] + extraGroomCostCents
				const cleanupRow = { duration: 0, duration_type: 'cleanup-finish', category: 'cleanup', price: cleanupPrice }
				rows.push(cleanupRow)
			}
		}

		if (service === 'cut' || service === 'haircut') {
			let trim = trim_amount?.toLowerCase() || 'clip'
			if (trim === 'shave' || trim === 'outline') {
				// If the shave or outline option is picked, then the row for 'haircut-outline will be considered on the duration table.
				let trimTime = table.find(
					(t) => t.duration_type === 'haircut-outline' && t.size.toLowerCase() === size.toLowerCase()
				)
				duration += trimTime.duration
				rows = addToRows(rows, trimTime)
			} else if (trim.includes('clip')) {
				// If the any of the clip options are picked, then the row for 'haircut-clip will be considered on the duration table.
				let trimTime = table.find(
					(t) => t.duration_type === 'haircut-clip' && t.size.toLowerCase() === size.toLowerCase()
				)
				duration += trimTime.duration
				rows = addToRows(rows, trimTime)
			} else {
				// If the option for 'Other- Breed Specific' is clicked, then the row for 'haircut-complex' will be considered on the duration table.
				let trimTime = table.find(
					(t) => t.duration_type === 'haircut-complex' && t.size.toLowerCase() === size.toLowerCase()
				)
				duration += trimTime.duration
				rows = addToRows(rows, trimTime)
			}

			if (isNewPricing) {
				const haircutPrice = household?.groomingRates[pet._id][`base_price_haircut`] + extraGroomCostCents

				const haircutRow = { duration: 0, duration_type: 'haircut-finish', category: 'haircut', price: haircutPrice }
				rows.push(haircutRow)
			}
		}


		return { duration, rows }
	}

	const renderDuration = ({ service }) => {

		if (facility?.services?.grooming?.pricing_method === 'new' && !table?.length) {
			return null
		}

		const { duration } = getDuration(service)

		return <Typography className={classes.theBod}>{duration}</Typography>
	}

	const calculateBasePrice = ({ service }) => {


		let price = household?.groomingRates[pet._id][`base_price_${service}`]

		const priceWithMultiplier = price * price_multiplier
		return priceWithMultiplier

	}

	const renderPrice = ({ service }) => {


		const price = calculateBasePrice({ service })
		return <Typography className={classes.theBod}>${(price / 100).toFixed(2)}</Typography>

	}

	const renderExtraTimeCost = ({ service, trim_amount, name, addon, price }) => {
		if (service === 'bath') return null
		return <Typography className={classes.theBod}>${(extraGroomCostCents / 100).toFixed(2)}</Typography>

	}

	const renderFinalPrice = ({ service, trim_amount, name, addon, price }) => {
		if (name && addon && price) return <Typography className={classes.theBod}>{price}</Typography>
		const { rows } = getDuration(service)
		let priceMin = 0
		if (facility?.services?.grooming?.pricing_method === 'new') {
			rows.forEach((row) => {
				if (row.price) priceMin += row.price
			})

		} else {
			rows.forEach((row) => {
				const p = prices[row.category]
				priceMin += p * row.duration
			})
		}

		const finalPriceCents = priceMin * price_multiplier
		return (
			<Typography className={classes.theBod}>{`$${(finalPriceCents / 100).toFixed(2)}`}</Typography>
		)
	}

	const shouldShowNa = facility?.services?.grooming?.pricing_method === 'new' ? false : true

	let sizeAndBreed = household?.groomingRates[pet._id]?.duration_table
	if (!sizeAndBreed) sizeAndBreed = `${size} ${pet.breeds?.join(', ')}`

	return (
		<div style={{ position: 'relative' }}>
			<Typography variant="body1" className={classes.sectionHeader}>
				Custom Grooming Duration / Pricing Guide
			</Typography>
			{toggle && (
				<IconButton
					className={classes.expand}
					style={{ position: 'absolute', top: -12, right: 0 }}
					onClick={toggle}>
					{open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
				</IconButton>
			)}
			{!pet?.table?.length && shouldShowNa ? (
				<Typography className={classes.theBod}>N/A</Typography>
			) : (
				<div
					style={{
						padding: 6,
						paddingLeft: 12,
						paddingRight: 12,
						backgroundColor: '#F9F9F9',
						borderRadius: 12
					}}>
					<MuiThemeProvider theme={tableAlternateDark}>
						<MaterialTable
							style={{
								boxShadow: 'none',
								borderRadius: '0px'
							}}
							key={extraGroomTime}
							options={{
								headerStyle: {
									color: '#1A1A35',
									textTransform: 'uppercase',
									fontWeight: 800,
									fontSize: '12px',
									lineHeight: '16px',
									padding: '11px 24px',
									backgroundColor: '#F9F9F9',
									paddingLeft: 12
									// textAlign: 'center'
								},
								actionsColumnIndex: -1,
								// toolbarButtonAlignment: 'left',
								// searchFieldAlignment: 'left',
								paging: false,
								rowStyle: (x) => {
									if (x.first) {
										return {
											borderTop: '1px solid #D0CFD2'
										}
									}
								}
							}}
							components={{
								Toolbar: (props) => (
									<div style={{ backgroundColor: '#F9F9F9', paddingTop: 16, paddingBottom: 16 }}>
										<Typography
											style={{
												fontWeight: '800',
												color: '#1A1A35',
												fontSize: 16,
												textTransform: 'uppercase'
											}}>
											{shouldShowNa ? `TABLE: ${table?.[0]?.table_id} ${size}` : `TABLE: ${sizeAndBreed}`}
										</Typography>
										{
											!table?.length ? (
												<Typography className={classes.theBod}>
													These prices reflect first-time grooming. To receive the most accurate pricing, please update the pet profile with breed, coat type, and size.
												</Typography>
											) : null
										}
									</div>
								)
							}}
							columns={[
								{
									title: 'Service Types',
									cellStyle: { textTransform: 'capitalize', paddingLeft: 12 },
									render: (data) => renderServiceTypes(data)
								},
								{
									title: 'Duration',
									render: (data) => renderDuration(data)
								},
								{
									title: 'Price',
									render: (data) => renderPrice(data)
								},
								...(extraGroomTime > 0
									? [
										{
											title: 'Extra Time Cost',
											render: (data) => renderExtraTimeCost(data)
										},
										{
											title: 'Final Price',
											render: (data) => renderFinalPrice(data)
										}
									]
									: [])
							]}
							data={tableData}
						/>
					</MuiThemeProvider>
				</div>
			)}
		</div>
	)
}

export default GroomingDurationTable
